import { AuctionSealedResponseDto } from '@tradgine/data-access/auction-sealed-flow';
import { CurrentOrganizationSealedBidResponseDto, SealedBidResponseDto } from '@tradgine/data-access/auction-sealed-flow';
import { SealedAuction, SealedBid } from '@tradgine/sealed-auction-trade';
import { TradingProcedureResponseDto, TradingProcedureStatusDto } from '@tradgine/data-access/tradgine-data';
import { SealedTradingProcedure, TradingProcedureStatus } from '@tradgine/sealed-auction-trade';
import { LotResponseDto } from '@tradgine/data-access/tradgine-data';
import { SealedLot } from '@tradgine/sealed-auction-trade';
import { TradingProcedureDocumentResponseDto, TradingProcedureRegistryInfoResponseDto, CurrentOrganizationOfferResponseDto, OfferResultResponseDto } from '@tradgine/data-access/tradgine-data';
import { SealedTradingProcedureDocument, SealedTradingProcedureRegistry, SealedOffer } from '@tradgine/sealed-auction-trade';
import { AuctionStatusEnum } from '@tradgine/data-access/auction-sealed-flow';
import { normalizeCurrency } from '../../../../core/constants/currency-map';
import { exhaustiveCheck } from '@tradgine/utils';

export function toTradingProcedureStatus(dto: TradingProcedureStatusDto): TradingProcedureStatus {
    switch (dto) {
        case TradingProcedureStatusDto.Draft:            return 'Draft';
        case TradingProcedureStatusDto.Published:        return 'Published';
        case TradingProcedureStatusDto.Active:           return 'Active';
        case TradingProcedureStatusDto.AwaitingDecision: return 'AwaitingDecision';
        case TradingProcedureStatusDto.Finished:         return 'Finished';
        case TradingProcedureStatusDto.Cancelled:        return 'Cancelled';
        default:                                         return exhaustiveCheck(dto);
    }
}

export function toSealedAuction(dto: AuctionSealedResponseDto): SealedAuction {
    return {
        id: dto.id,
        lotId: dto.id, // lotId === auctionId (упрощение от бэкенда)
        tradingProcedureId: dto.tradingProcedureId,
        status: dto.status === AuctionStatusEnum.Active ? 'active' : 'finished',
        bidCount: dto.bidCount,
        leadingSealedBidId: dto.leadingSealedBidId ?? null,
    };
}

export function toSealedBid(dto: CurrentOrganizationSealedBidResponseDto | SealedBidResponseDto, lotId: number): SealedBid {
    return {
        id: dto.id,
        auctionId: dto.auctionId,
        lotId: lotId,
        organizationId: dto.organizationId,
        organizationName: dto.organizationName ?? '',
        amount: dto.amount,
    };
}

export function toSealedTradingProcedure(dto: TradingProcedureResponseDto): SealedTradingProcedure {
    return {
        id: dto.id,
        name: dto.name,
        status: toTradingProcedureStatus(dto.status),
        showSellerName: dto.showSellerName,
        sellerOrganizationName: dto.sellerOrganizationName,
        sellerOrganizationTypeName: dto.sellerOrganizationTypeName,
        auctionExtensionSeconds: dto.auctionExtensionSeconds,
        bidStepAmount: dto.bidStepAmount,
        currency: normalizeCurrency(dto.currency),
        sellerDescription: dto.sellerDescription,
        platformNote: dto.platformNote,
        startDateTime: new Date(dto.startDateTime),
        endDateTime: new Date(dto.endDateTime),
        sellerOrganizationId: dto.sellerOrganizationId ?? 0,
        createdDateTime: new Date(dto.createdDateTime),
    };
}

export function toSealedLot(dto: LotResponseDto): SealedLot {
    return {
        id: dto.id,
        tradingProcedureId: dto.tradingProcedureId,
        name: dto.name,
        minPrice: dto.minPrice,
        debtAmount: dto.debtAmount,
        principalAmount: dto.principalAmount,
        averageDpd: dto.averageDpd,
        contractCount: dto.contractCount,
        documentType: dto.documentType,
        portfolioType: dto.portfolioType,
        debtStage: dto.debtStage,
        finalOfferId: dto.finalOfferId ?? null,
    };
}

export function toSealedTradingProcedureDocument(dto: TradingProcedureDocumentResponseDto): SealedTradingProcedureDocument {
    return {
        id: dto.id,
        tradingProcedureId: dto.tradingProcedureId,
        fileName: dto.fileName,
        extension: dto.extension,
        documentUrl: dto.documentUrl,
        description: dto.description,
        contentLength: dto.contentLength,
        createdDateTime: dto.createdDateTime,
    };
}

export function toSealedOfferFromSellerDto(dto: CurrentOrganizationOfferResponseDto): SealedOffer {
    return {
        id: dto.id ?? 0,
        auctionId: dto.lotId, // auctionId === lotId в sealed аукционе
        organizationId: dto.organizationId,
        postedDateTime: dto.postedDateTime ?? '',
        amount: dto.amount,
    };
}

export function toSealedOfferFromResultDto(dto: OfferResultResponseDto): SealedOffer {
    return {
        id: dto.id,
        auctionId: dto.lotId, // auctionId === lotId в sealed аукционе
        organizationId: dto.organizationId,
        postedDateTime: dto.postedDateTime,
        amount: dto.amount,
    };
}

export function toSealedTradingProcedureRegistry(dto: TradingProcedureRegistryInfoResponseDto): SealedTradingProcedureRegistry {
    return {
        tradingProcedureId: dto.tradingProcedureId,
        fileName: dto.fileName,
        extension: dto.extension,
        description: dto.description,
        contentLength: dto.contentLength,
        createdDateTime: dto.createdDateTime,
    };
}

